import axios from 'axios'
import VueAxios from 'vue-axios'
import Vue from 'vue'
import i18n from '../plugins/i18n'
import router from '../router'
import contentService from '@/api/contentService'

Vue.use(VueAxios, axios)

function decodeJWTToken(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload;
  } catch (e) {
    console.error("Error decoding JWT token:", e);
    return null;
  }
}

function extractUsernameFromToken(token) {
  const payload = decodeJWTToken(token);
  if (!payload) return null;
  
  return payload.username || payload.sub || payload.preferred_username || payload.user || null;
}

function extractEmailFromToken(token) {
  const payload = decodeJWTToken(token);
  if (!payload) return null;
  
  // Common JWT email fields
  return payload.email || payload.emailAddress || payload.mail || null;
}

const state = {
  loading: true,
  user: initializeUser(),
  tenant: initializeTenant(),
  isLoginPage: false,
  loginFailed: false,
  passwordResetEmail: null,
  codeVerified: false,
  currentResetCode: null
};

function initializeTenant() {
  const tenantId = localStorage.getItem("tenantId");
  return tenantId || null;
}

function initializeUser() {
  const token = localStorage.getItem("token");
  const storedUser = localStorage.getItem("user");
  
  if (token && storedUser) {
    try {
      return JSON.parse(storedUser);
    } catch (e) {
      console.error("Error parsing stored user:", e);
      localStorage.removeItem("user");
    }
  }
  
  return null;
}

const getters = {
  isLoading() {
    return state.loading;
  },
  isLoginPage() {
    return state.isLoginPage;
  },
  getUser() {
    return state.user;
  },
  getTenant() {
    return state.tenant;
  },
  getLoginFailed() {
    return state.loginFailed;
  },
  getPasswordResetEmail() {
    return state.passwordResetEmail;
  },
  getCodeVerified() {
    return state.codeVerified;
  }
};

const mutations = {
  setLoading(state, status) {
    state.loading = status;
  },
  setUser(state, user) {
    state.user = user;
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  },
  setTenant(state, tenant) {
    state.tenant = tenant;
  },
  setIsLoginPage(state, flag) {
    state.isLoginPage = flag;
  },
  setLoginFailed(state, flag) {
    state.loginFailed = flag;
  },
  setPasswordResetEmail(state, email) {
    state.passwordResetEmail = email;
  },
  setCodeVerified(state, verified) {
    state.codeVerified = verified;
  },
  setCurrentResetCode(state, code) {
    state.currentResetCode = code;
  },
  clearPasswordResetState(state) {
    state.passwordResetEmail = null;
    state.codeVerified = false;
    state.currentResetCode = null;
  }
};

const actions = {
  enterApp({ commit }) {
    commit("setLoading", false);
  },
  async login({ commit, dispatch }, data) {
    commit("setLoginFailed", false);
    let authenticated = await axios.post(`${i18n.t('app.content_service')}${i18n.t('app.authentication_controller')}/authenticate`, { username: data.username, password: data.password }).catch( error => {
      console.log(error);
      commit("setLoginFailed", true);
    });
    if (authenticated && authenticated.status == 200 && authenticated.data.token) {
        // Extract actual username from JWT token instead of using login input
        const actualUsername = extractUsernameFromToken(authenticated.data.token);
        const username = actualUsername || data.username; // Fallback to input if token parsing fails
        
        // Check if email is provided in the authentication response or JWT token
        const emailFromToken = extractEmailFromToken(authenticated.data.token);
        let email = authenticated.data.email || authenticated.data.emailAddress || emailFromToken || null;
        
        // Store initial user info
        commit("setUser", { name: username, email: email, image: "https://cdn.vuetifyjs.com/images/john.jpg" });
        commit("setTenant", authenticated.data.tenantId);
        localStorage.setItem("token", authenticated.data.token);
        localStorage.setItem("tenantId", authenticated.data.tenantId)
        commit("setIsLoginPage", false);
        
        // Fetch email if not already available
        if (!email && username) {
            try {
                const emailResponse = await axios.get(`${i18n.t('app.content_service')}auth/user/email`, {
                    headers: {
                        'Authorization': `Bearer ${authenticated.data.token}`
                    },
                    params: {
                        username: username
                    }
                });
                
                if (emailResponse.status === 200 && emailResponse.data) {
                    // Update user with fetched email
                    const currentUser = state.user;
                    commit("setUser", { ...currentUser, email: emailResponse.data });
                }
            } catch (error) {
                console.error("Error fetching user email:", error);
            }
        }
        
        if (authenticated.data.tenantId) {
            try {
                await dispatch('selectTenant', authenticated.data.tenantId, { root: true });
            } catch (error) {
                console.error("Error selecting tenant:", error);
            }
        }
        
        // Check if user is admin and needs tenant selection
        if (username === 'admin') {
            router.push("/tenant-selection");
        } else {
            router.push("/");
        }
    } else {
        commit("setLoginFailed", true);
    }
  },
  setLoginPage({ commit }, flag) {
    commit("setIsLoginPage", flag);
  },
  async logout({ commit, getters }) {
    try {
      const token = localStorage.getItem("token");
      const username = getters.getUser?.name;
      const tenantId = localStorage.getItem("tenantId");
      
      if (token) {
        await axios.post(`${i18n.t('app.content_service')}${i18n.t('app.authentication_controller')}/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          params: {
            username: username,
            tenantId: tenantId
          }
        });
      }
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('user');
      commit("setUser", null);
      commit("setTenant", null);
      router.push("/login");
    }
  },
  async updateEmail({ commit, getters }, updateData) {
    try {
      const currentUser = getters.getUser;
      
      if (!currentUser) {
        throw new Error("User not authenticated");
      }
      
      // Handle both old format (just email string) and new format (object with email and targetUsername)
      const email = typeof updateData === 'string' ? updateData : updateData.email;
      const targetUsername = updateData.targetUsername || currentUser.name;
      
      const response = await contentService.post(`auth/updateEmail?username=${targetUsername}&newEmail=${email}`);
      
      if (response.status === 200) {
        // Update user state with new email only if updating current user's email
        if (targetUsername === currentUser.name) {
          commit("setUser", { ...currentUser, email: email });
        }
        return { success: true };
      }
    } catch (error) {
      console.error("Error updating email:", error);
      return { success: false, error: error.response?.data || error.message || "Failed to update email" };
    }
  },
  async requestPasswordReset({ commit }, email) {
    try {
      const response = await contentService.post('auth/requestReset?email=' + email);
      if (response.status === 200) {
        commit('setPasswordResetEmail', email);
        commit('setCodeVerified', false);
        return { success: true };
      }
    } catch (error) {
      console.error("Error requesting password reset:", error);
      return { success: false, error: error.response?.data || "Failed to send verification code" };
    }
  },
  async verifyResetCode({ commit }, { email, code }) {
    try {
      const response = await contentService.post('auth/verifyCode?email=' + email + '&code=' + code);
      if (response.status === 200) {
        commit('setCodeVerified', true);
        commit('setCurrentResetCode', code);
        return { success: true };
      }
    } catch (error) {
      console.error("Error verifying reset code:", error);
      
      return { success: false, error: error.response?.data || "Invalid verification code" };
    }
  },
  async resetPassword({ commit, state }, { email, newPassword }) {
    try {
      if (!state.codeVerified || !state.currentResetCode) {
        return { success: false, error: "Please verify your code first" };
      }
      
      const response = await contentService.post('auth/resetPassword', { email, code: state.currentResetCode, newPassword });
      if (response.status === 200) {
        commit('clearPasswordResetState');
        return { success: true };
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      return { success: false, error: error.response?.data || "Failed to reset password" };
    }
  },
  async checkAuth({ commit, state }) {
    // This action is called when re-authenticating after loginAsUser
    // It will re-fetch the current user info if needed
    return true;
  },
  async createUser(context, userData) {
    try {
      const response = await contentService.post('auth/createUser', userData);
      
      if (response.status === 200 || response.status === 201) {
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error("Error creating user:", error);
      
      return { 
        success: false, 
        error: error.response?.data?.error || error.message || "Failed to create user" 
      };
    }
  },
  async fetchUsers() {
    try {
      const response = await contentService.get('auth/users');
      
      if (response.status === 200 && response.data) {
        return { success: true, data: response.data };
      }
      
      return { success: false, data: [] };
    } catch (error) {
      console.error("Error fetching users:", error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || "Failed to fetch users",
        data: []
      };
    }
  },
  async deleteUser(context, username) {
    try {
      const response = await contentService.delete('auth/deleteUser?username=' + username);
      
      if (response.status === 200 || response.status === 204) {
        return { success: true };
      }
      
      return { success: false, error: "Failed to delete user" };
    } catch (error) {
      console.error("Error deleting user:", error);
      return { 
        success: false, 
        error: error.response?.data || error.message || "Failed to delete user" 
      };
    }
  }
};

export default {
  state: state,
  mutations: mutations,
  actions: actions,
  getters: getters,
};
